import { useEffect, useState } from 'react'
import { useParams, Link } from 'react-router-dom'
import ReactMarkdown from 'react-markdown'
import remarkGfm from 'remark-gfm'
import { caseStudyService } from '../../services'
import achievementToast from '../../components/AchievementToast'
import CelebrationModal from '../../components/CelebrationModal'
import DiscussionList from '../../components/discussions/DiscussionList'
import { useProgressStore } from '../../stores/progressStore'
import {
  ArrowLeftIcon,
  CheckCircleIcon,
  DocumentTextIcon,
  LightBulbIcon,
  ChartBarIcon,
  ChatBubbleLeftRightIcon,
  ClockIcon,
  AcademicCapIcon,
} from '@heroicons/react/24/outline'
import { CheckCircleIcon as CheckCircleSolid } from '@heroicons/react/24/solid'

export default function CaseStudyView() {
  const { id } = useParams()
  const { fetchProgress, getCompletionPercentage } = useProgressStore()
  const [caseStudy, setCaseStudy] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [submitting, setSubmitting] = useState(false)
  const [completing, setCompleting] = useState(false)
  const [submissionText, setSubmissionText] = useState('')
  const [showSubmitForm, setShowSubmitForm] = useState(false)
  const [showCelebrationModal, setShowCelebrationModal] = useState(false)
  const [earnedBadges, setEarnedBadges] = useState([])

  useEffect(() => {
    fetchCaseStudy()
  }, [id])

  const fetchCaseStudy = async () => {
    try {
      setLoading(true)
      const data = await caseStudyService.getById(id)
      setCaseStudy(data)
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to load case study')
    } finally {
      setLoading(false)
    }
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    try {
      setSubmitting(true)
      await caseStudyService.submit(id, {
        submission_text: submissionText,
      })
      achievementToast.success('Case study submitted successfully! 🎉')
      setShowSubmitForm(false)
      setSubmissionText('')
      fetchCaseStudy()
    } catch (err) {
      achievementToast.error(err.response?.data?.message || 'Failed to submit case study')
    } finally {
      setSubmitting(false)
    }
  }

  const handleMarkComplete = async () => {
    if (completing || caseStudy?.user_progress?.status === 'completed') return
    
    try {
      setCompleting(true)
      const response = await caseStudyService.updateProgress(id, {
        status: 'completed',
        progress_percentage: 100,
      })

      achievementToast.success('Case study completed! 🎉')

      if (response.earned_badges && response.earned_badges.length > 0) {
        setEarnedBadges(response.earned_badges)
        
        response.earned_badges.forEach((badge, index) => {
          setTimeout(() => {
            achievementToast.badge(badge)
          }, (index + 1) * 1500)
        })

        setTimeout(() => {
          setShowCelebrationModal(true)
        }, response.earned_badges.length * 1500 + 1000)
      }

      await fetchCaseStudy()
      await fetchProgress()

      const newCompletionPercentage = getCompletionPercentage()
      if ([25, 50, 75, 100].includes(newCompletionPercentage)) {
        setTimeout(() => {
          achievementToast.milestone(newCompletionPercentage)
        }, response.earned_badges?.length ? response.earned_badges.length * 1500 + 2000 : 2000)
      }
    } catch (err) {
      console.error('Failed to mark case study as complete:', err)
      achievementToast.error('Failed to mark case study as complete. Please try again.')
    } finally {
      setCompleting(false)
    }
  }

  const getDifficultyColor = (level) => {
    switch (level) {
      case 'beginner':
        return 'bg-green-100 text-green-800'
      case 'intermediate':
        return 'bg-yellow-100 text-yellow-800'
      case 'advanced':
        return 'bg-red-100 text-red-800'
      default:
        return 'bg-gray-100 text-gray-800'
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-gray-600">Loading case study...</div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-700">
        {error}
      </div>
    )
  }

  if (!caseStudy) {
    return (
      <div className="text-center py-12">
        <p className="text-gray-600">Case study not found</p>
        <Link to="/modules" className="text-indigo-600 hover:underline mt-4 inline-block">
          Back to modules
        </Link>
      </div>
    )
  }

  const isCompleted = caseStudy.user_progress?.status === 'completed'

  return (
    <div className="max-w-5xl mx-auto">
      {/* Back Button */}
      <Link
        to={`/modules/${caseStudy.module_id}`}
        className="inline-flex items-center text-indigo-600 hover:text-indigo-700 mb-6"
      >
        <ArrowLeftIcon className="w-4 h-4 mr-2" />
        Back to Module
      </Link>

      {/* Header */}
      <div className="bg-gradient-to-r from-indigo-50 to-purple-50 rounded-xl border border-indigo-200 p-8 mb-6">
        <div className="flex items-start justify-between mb-4">
          <div>
            <div className="text-sm font-mono text-indigo-600 mb-2">
              {caseStudy.code}
            </div>
            <h1 className="text-3xl font-bold text-gray-900 mb-2">
              {caseStudy.title}
            </h1>
            <p className="text-lg text-gray-700">
              {caseStudy.description}
            </p>
          </div>
          {isCompleted && (
            <CheckCircleSolid className="h-12 w-12 text-green-500 flex-shrink-0" />
          )}
        </div>

        {/* Meta Information */}
        <div className="flex flex-wrap gap-4 text-sm">
          <div className="flex items-center text-gray-700">
            <ClockIcon className="w-5 h-5 mr-2" />
            <span>{caseStudy.estimated_minutes || caseStudy.estimated_time_minutes || 30} minutes</span>
          </div>
          <span className={`px-3 py-1 rounded-full text-sm font-medium ${getDifficultyColor(caseStudy.difficulty_level)}`}>
            {caseStudy.difficulty_level || 'intermediate'}
          </span>
        </div>
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        {/* Main Content */}
        <div className="lg:col-span-2 space-y-6">
          {/* Scenario */}
          {caseStudy.scenario && (
            <div className="bg-white rounded-xl border border-gray-200 p-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-4 flex items-center">
                <DocumentTextIcon className="h-6 w-6 mr-2 text-indigo-600" />
                Business Scenario
              </h2>
              <div className="prose prose-lg max-w-none">
                <ReactMarkdown remarkPlugins={[remarkGfm]}>
                  {caseStudy.scenario}
                </ReactMarkdown>
              </div>
            </div>
          )}

          {/* Context */}
          {caseStudy.context && (
            <div className="bg-white rounded-xl border border-gray-200 p-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-4 flex items-center">
                <ChartBarIcon className="h-6 w-6 mr-2 text-blue-600" />
                Context & Background
              </h2>
              <div className="prose prose-lg max-w-none">
                <ReactMarkdown remarkPlugins={[remarkGfm]}>
                  {caseStudy.context}
                </ReactMarkdown>
              </div>
            </div>
          )}

          {/* Challenge */}
          {caseStudy.challenge && (
            <div className="bg-orange-50 rounded-xl border border-orange-200 p-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-4 flex items-center">
                <LightBulbIcon className="h-6 w-6 mr-2 text-orange-600" />
                The Challenge
              </h2>
              <div className="prose prose-lg max-w-none">
                <ReactMarkdown remarkPlugins={[remarkGfm]}>
                  {caseStudy.challenge}
                </ReactMarkdown>
              </div>
            </div>
          )}

          {/* Discussion Questions */}
          {caseStudy.discussion_questions && caseStudy.discussion_questions.length > 0 && (
            <div className="bg-purple-50 rounded-xl border border-purple-200 p-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-4 flex items-center">
                <ChatBubbleLeftRightIcon className="h-6 w-6 mr-2 text-purple-600" />
                Discussion Questions
              </h2>
              <ul className="space-y-3">
                {caseStudy.discussion_questions.map((question, index) => (
                  <li key={index} className="flex items-start">
                    <span className="text-purple-600 font-bold mr-3">{index + 1}.</span>
                    <span className="text-gray-700">{question}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Submission Form */}
          {!isCompleted && (
            <div className="bg-white rounded-xl border border-gray-200 p-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">
                Submit Your Analysis
              </h2>

              {!showSubmitForm ? (
                <button
                  onClick={() => setShowSubmitForm(true)}
                  className="w-full py-4 bg-indigo-600 text-white rounded-lg font-semibold hover:bg-indigo-700 transition-colors"
                >
                  Start Your Submission
                </button>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div>
                    <label htmlFor="submission" className="block text-sm font-semibold text-gray-700 mb-2">
                      Your Analysis & Solution
                    </label>
                    <textarea
                      id="submission"
                      rows={12}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                      placeholder="Describe your analysis, approach, and proposed solution..."
                      value={submissionText}
                      onChange={(e) => setSubmissionText(e.target.value)}
                      required
                    />
                  </div>

                  <div className="flex gap-3">
                    <button
                      type="submit"
                      disabled={submitting}
                      className="flex-1 px-6 py-3 bg-indigo-600 text-white rounded-lg font-semibold hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                    >
                      {submitting ? 'Submitting...' : 'Submit Analysis'}
                    </button>
                    <button
                      type="button"
                      onClick={() => setShowSubmitForm(false)}
                      className="px-6 py-3 border-2 border-gray-300 text-gray-700 rounded-lg font-semibold hover:bg-gray-50 transition-colors"
                    >
                      Cancel
                    </button>
                  </div>
                </form>
              )}
            </div>
          )}
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Learning Outcomes */}
          {caseStudy.learning_outcomes && caseStudy.learning_outcomes.length > 0 && (
            <div className="bg-white rounded-xl border border-gray-200 p-6">
              <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center">
                <AcademicCapIcon className="h-5 w-5 mr-2 text-indigo-600" />
                Learning Outcomes
              </h3>
              <ul className="space-y-3">
                {caseStudy.learning_outcomes.map((outcome, idx) => (
                  <li key={idx} className="flex items-start text-sm">
                    <CheckCircleIcon className="h-5 w-5 mr-2 text-green-600 flex-shrink-0 mt-0.5" />
                    <span className="text-gray-700">{outcome}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Mark as Complete */}
          <div className="bg-white rounded-xl border border-gray-200 p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-3">
              {isCompleted ? 'Completed! 🎉' : 'Mark as Complete'}
            </h3>
            <p className="text-sm text-gray-600 mb-4">
              {isCompleted 
                ? 'Great work on this case study!' 
                : 'Complete this case study to track your progress.'}
            </p>
            <button
              onClick={handleMarkComplete}
              disabled={completing || isCompleted}
              className={`w-full px-6 py-3 rounded-lg font-semibold flex items-center justify-center transition-all ${
                isCompleted
                  ? 'bg-green-100 text-green-700 cursor-not-allowed'
                  : 'bg-indigo-600 text-white hover:bg-indigo-700 hover:shadow-lg'
              } ${completing ? 'opacity-50 cursor-wait' : ''}`}
            >
              {completing ? (
                <>
                  <svg className="animate-spin -ml-1 mr-2 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Processing...
                </>
              ) : isCompleted ? (
                <>
                  <CheckCircleSolid className="w-5 h-5 mr-2" />
                  Completed
                </>
              ) : (
                <>
                  <CheckCircleIcon className="w-5 h-5 mr-2" />
                  Mark as Complete
                </>
              )}
            </button>
          </div>
        </div>
      </div>

      {/* Discussions Section */}
      <div className="bg-white rounded-xl border border-gray-200 p-8 mt-6">
        <h2 className="text-2xl font-bold text-gray-900 mb-4 flex items-center">
          <ChatBubbleLeftRightIcon className="h-6 w-6 mr-2 text-indigo-600" />
          Case Study Discussions
        </h2>
        <p className="text-sm text-gray-600 mb-6">
          Share insights, debate solutions, and learn from other students' perspectives.
        </p>
        <DiscussionList 
          discussionableType="CaseStudy" 
          discussionableId={id} 
        />
      </div>

      {/* Celebration Modal */}
      <CelebrationModal
        isOpen={showCelebrationModal}
        onClose={() => setShowCelebrationModal(false)}
        badges={earnedBadges}
        title="Case Study Complete! 📊"
        message="Excellent analysis and critical thinking!"
        confettiType="stars"
      />
    </div>
  )
}
